/**
 * AgentActivityPanel - Main panel showing all active AI agents
 * Displays 6 agent cards in a grid with real-time status updates
 */

import React, { useState, useEffect } from 'react';
import { ClaimPipeline, AgentStatus, AI_AGENTS, PIPELINE_STAGES } from './types';
import AgentCard from './AgentCard';
import AgentTimeline from './AgentTimeline';
import { useAgentUpdates } from './hooks/useAgentUpdates';
import { Bot, RefreshCw, ChevronDown, ChevronUp, Activity, Wifi, WifiOff } from 'lucide-react';

interface AgentActivityPanelProps {
  claimId?: string;  // If provided, shows specific claim's pipeline
  showTimeline?: boolean;
  defaultExpanded?: boolean;
  onAgentClick?: (agentId: string, claimId?: string) => void;
}

export default function AgentActivityPanel({ 
  claimId, 
  showTimeline = true,
  defaultExpanded = true,
  onAgentClick 
}: AgentActivityPanelProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const { pipeline, loading, error, refresh, connected } = useAgentUpdates(claimId);
  
  // Get the 6 most relevant agents to display (running first, then queued)
  const getDisplayAgents = (): AgentStatus[] => {
    if (!pipeline?.agents) return [];
    
    const running = pipeline.agents.filter(a => a.status === 'running');
    const waitingHuman = pipeline.agents.filter(a => a.status === 'waiting_human');
    const complete = pipeline.agents.filter(a => a.status === 'complete').slice(-2);
    const queued = pipeline.agents.filter(a => a.status === 'queued' || a.status === 'idle').slice(0, 3);
    
    // Priority: running > waiting_human > recent complete > queued
    return [...running, ...waitingHuman, ...complete, ...queued].slice(0, 6);
  };
  
  const displayAgents = getDisplayAgents();
  const runningAgents = displayAgents.filter(a => a.status === 'running');
  const hasActivity = runningAgents.length > 0 || pipeline?.pendingHumanReview;
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div 
        className={`
          flex items-center justify-between px-4 py-3 cursor-pointer
          ${hasActivity ? 'bg-gradient-to-r from-blue-50 to-cyan-50' : 'bg-slate-50'}
          hover:bg-opacity-80 transition-colors
        `}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className={`
            p-2 rounded-lg
            ${hasActivity ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'}
          `}>
            <Bot className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              AI Agents
              {hasActivity && (
                <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">
                  <Activity className="w-3 h-3 animate-pulse" />
                  {runningAgents.length} active
                </span>
              )}
            </h3>
            <p className="text-xs text-slate-500">
              {pipeline?.claimName 
                ? `Processing: ${pipeline.claimName}` 
                : '14 AI agents working on your claims'
              }
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Connection status indicator */}
          <span 
            className={`flex items-center gap-1 text-xs ${connected ? 'text-emerald-600' : 'text-slate-400'}`}
            title={connected ? 'Real-time updates active' : 'Using polling updates'}
          >
            {connected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
          </span>
          
          {pipeline?.pendingHumanReview ? (
            <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
              {pipeline.pendingHumanReview} need review
            </span>
          ) : null}
          <button 
            className="p-1 hover:bg-white/50 rounded transition-colors"
            onClick={(e) => { e.stopPropagation(); refresh(); }}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 text-slate-500 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-slate-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-slate-400" />
          )}
        </div>
      </div>
      
      {/* Content */}
      {expanded && (
        <div className="p-4">
          {error ? (
            <div className="text-center py-8 text-slate-500">
              <p>{error}</p>
              <button 
                className="mt-2 text-blue-600 hover:underline text-sm"
                onClick={refresh}
              >
                Try again
              </button>
            </div>
          ) : loading && !pipeline ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 text-blue-500 animate-spin" />
            </div>
          ) : (
            <>
              {/* Timeline view */}
              {showTimeline && pipeline?.agents && (
                <div className="mb-6 pb-4 border-b border-slate-100">
                  <AgentTimeline 
                    agents={pipeline.agents}
                    currentAgent={pipeline.currentAgent}
                    onAgentClick={(id) => onAgentClick?.(id, claimId)}
                  />
                </div>
              )}
              
              {/* Agent cards grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {displayAgents.length > 0 ? (
                  displayAgents.map(agent => (
                    <AgentCard 
                      key={agent.agentId}
                      agent={agent}
                      onClick={() => onAgentClick?.(agent.agentId, claimId)}
                    />
                  ))
                ) : (
                  <div className="col-span-full text-center py-6 text-slate-500">
                    <Bot className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No active AI agents</p>
                    <p className="text-xs">Upload documents to start processing</p>
                  </div>
                )}
              </div>
              
              {/* Next up info */}
              {pipeline?.estimatedCompletion && (
                <div className="mt-4 pt-4 border-t border-slate-100 text-sm text-slate-600">
                  <span className="font-medium">Estimated completion:</span>{' '}
                  {new Date(pipeline.estimatedCompletion).toLocaleTimeString()}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
